import React, { useContext, useEffect, useState, useRef } from 'react';
import { Observer } from 'mobx-react-lite';
import FadeIn from 'react-fade-in';
import momentLocalizer from 'react-widgets-moment';
import { GlobalHotKeys } from 'react-hotkeys';
import DateBox from 'devextreme-react/date-box';
import { Multiselect } from 'react-widgets'
import moment from 'moment';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModalComponent';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';

import TimeslotScheduleCreateStore from '../../../../stores/TimeslotScheduleCreateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';
import * as uh from '../../../../utilities/userHelper';

import api from '../../../../api';

import './NewTimeslotSchedule.scss';

moment.locale('en');
momentLocalizer();

function NewTimeslotSchedule(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const confirmModalRef = useRef(null);
    const timeslotSchedule = useContext(TimeslotScheduleCreateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [businessSchedule, setBusinessSchedule] = useState([]);
    const [bookingTypes, setBookingTypes] = useState([]);
    const [repeatType, setRepeatType] = useState('');
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        quickDrawerFocus(props.drawer);

        Promise.all([
            api.BookingTypes.all(),
            api.BusinessSchedules.get()
        ])
            .then(response => {
                if (isMounted.current) {
                    const bookingTypesData = response[0] && response[0].data ? response[0].data : null;
                    const businessSchedulesData = response[1] && response[1].data ? response[1].data : null;

                    if (bookingTypesData) {
                        setBookingTypes(bookingTypesData);
                    }

                    if (businessSchedulesData) {
                        const todayBusinessSchedule = businessSchedulesData.some(s => s.dayOfWeek === moment(timeslotSchedule.start).format('dddd')) ? businessSchedulesData.filter(s => s.dayOfWeek === moment(timeslotSchedule.start).format('dddd'))[0] : null;
                        setBusinessSchedule(todayBusinessSchedule);
                    }
                }
            })
            .finally(() => {
                if (isMounted.current) {
                    setIsReady(true);
                }
            })

        return () => {
            isMounted.current = false;
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (timeslotSchedule.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleTimeslotStartChange = element => {
        const value = element.value;
        const newStart = value ? moment(value) : null;

        timeslotSchedule.start = newStart ? newStart.format('HH:mm:ss') : null;
        timeslotSchedule.end = newStart && timeslotSchedule.duration ? newStart.clone().add(timeslotSchedule.duration, 'minutes').format('HH:mm:ss') : null;
        timeslotSchedule.hasUnsavedChanges = true;
    }

    const handleDurationChange = event => {
        timeslotSchedule.duration = parseInt(event.target.value, 10);
        timeslotSchedule.end = timeslotSchedule.start ? moment(timeslotSchedule.start, 'HH:mm:ss').add(timeslotSchedule.duration, 'minutes').format('HH:mm:ss') : null;
        timeslotSchedule.hasUnsavedChanges = true;
    }

    const handleBookingTypeChange = (tag, metadata) => {
        switch (metadata.action) {
            case 'insert':
                if (!timeslotSchedule.bookingTypes.some(a => a.id === metadata.dataItem.id)) {
                    timeslotSchedule.bookingTypes.push(metadata.dataItem);
                }
                break;

            case 'remove':
                const index = timeslotSchedule.bookingTypes.findIndex(a => a.id === metadata.dataItem.id);
                if (index !== -1) {
                    timeslotSchedule.bookingTypes.splice(index, 1);
                }
                break;

            default:
                break;
        }

        timeslotSchedule.hasUnsavedChanges = true;
    }

    const handleRepeatTypeChange = event => {
        const { value } = event.target;

        setRepeatType(value);

        if (repeatType === 'until') {
            timeslotSchedule.repeatUntil = businessSchedule && businessSchedule.end ? moment(`${moment().format('YYYY-MM-DD')} ${businessSchedule.end.format('HH:mm')}`, 'YYYY-MM-DD HH:mm') : null;
        }
        else {
            timeslotSchedule.repeatUntil = null;
        }

        timeslotSchedule.hasUnsavedChanges = true;
    }

    const handleTimeslotRepeatUntilChange = element => {
        const value = element.value;
        const newRepeatUntil = value ? moment(value) : null;

        timeslotSchedule.repeatUntil = newRepeatUntil ? newRepeatUntil.format('HH:mm:ss') : null;
        timeslotSchedule.hasUnsavedChanges = true;
    }

    const handleDaysOfWeekChange = (event) => {
        const dayOfWeek = event.target.value;
        const index = timeslotSchedule.daysOfWeek.findIndex(d => d === dayOfWeek);

        if ((timeslotSchedule.daysOfWeek.length === 1 && timeslotSchedule.daysOfWeek[0] !== dayOfWeek) || timeslotSchedule.daysOfWeek.length > 1) {
            if (index === -1) {
                timeslotSchedule.daysOfWeek.push(dayOfWeek);
            } else {
                timeslotSchedule.daysOfWeek.splice(index, 1);
            }
        }

        timeslotSchedule.hasUnsavedChanges = true;
    }

    const handleDelete = event => {
        confirmModalRef.current.show({
            icon: <i className='fal fa-calendar-times text-danger mr-2'></i>,
            message: <>Continue to delete the<span className='fw-500'>&nbsp;{moment(timeslotSchedule.start, 'HH:mm').format('h:mm a')} timeslot</span>? </>,
            option1ClassName: 'btn btn-danger shadow-0 bootbox-accept',
            option1Text: 'Delete',
            cancelText: 'Cancel',
            onOption1Click: handleDeleteConfirm,
            onCancel: handleDeleteCancel
        })
    }

    const handleDeleteConfirm = (event) => {
        timeslotSchedule.delete()
            .then(() => {
                if (isMounted.current) {
                    confirmModalRef.current.close();

                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, { updated: true, data: { id: timeslotSchedule.id } });
                    }
                }
            })
    }

    const handleDeleteCancel = () => {
        confirmModalRef.current.close();
    }

    const handleSubmit = event => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current)) {
            timeslotSchedule.save(true)
                .then(() => {
                    if (isMounted.current) {
                        if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                            props.onSuccess(event, { updated: true, data: { id: timeslotSchedule.id } });
                        }
                    }
                })
        }
    }

    const renderUser = () => {
        if (!timeslotSchedule) return;

        const user = uh.getById(timeslotSchedule.userId);

        return user ? <div
            className='profile-wrapper pr-0'
        >
            <div className='profile'>
                <span
                    className={`profile-image rounded-circle d-block fw-500` + (user && !user.profilePictureUri ? ` profile-initials bg-color${user.color}-500` : '')}
                    style={user && user.profilePictureUri ? {
                        backgroundImage: `url(${user.profilePictureUri})`,
                        backgroundSize: 'cover',
                    } : null}
                    title={user ? user.fullName : 'System'}>
                    {user && !user.profilePictureUri ? <div className='d-initials'>{user.initials}</div> : null}
                </span>
            </div>
            <div className='description flex-1'>
                <div className='name'>{user.fullName}</div>
            </div>
        </div> : null
    }

    const getBookingTypes = () => {
        const filteredBookingTypes = bookingTypes.filter(a1 => timeslotSchedule.bookingTypes.filter(a2 => a2.id === a1.id).length === 0);
        return filteredBookingTypes;
    }

    return <>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <Observer>{() =>
                <>
                    {
                        (props.drawer === quickDrawer.drawerOpened) ?
                            <GlobalHotKeys
                                keyMap={{
                                    close: ['esc'],
                                }}
                                handlers={{
                                    close: event => {
                                        handleCancel(event)
                                    },
                                }}
                                allowChanges={true}
                            /> : null
                    }
                    <fieldset disabled={timeslotSchedule.isSaving}>
                        <div className='quick-drawer'>
                            <QuickDrawerHeader
                                drawer={props.drawer}
                                icon={oh.getIcon('appointment', 'update')}
                                action='New'
                                category='Online Booking Timeslot'
                                className='appointments'
                                onCancel={handleCancel}
                            />
                            <div className='quick-drawer-body'>
                                {
                                    timeslotSchedule.isReady && isReady ?
                                        <FadeIn>
                                            <div className='update-timeslot-schedule body-content'>
                                                <section className='resource'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-0'>
                                                                <label><small>With</small></label>
                                                                {renderUser()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <label className='required'><small>Time</small></label>
                                                        </div>
                                                    </div>
                                                    <div className='row'>
                                                        <div className='col-6'>
                                                            <div className='form-group mb-0 validate validate-required'>
                                                                <Observer>{() =>
                                                                    <DateBox
                                                                        id='update-timeslot-schedule-start'
                                                                        type='time'
                                                                        displayFormat={'hh:mm a'}
                                                                        min={businessSchedule ? moment(businessSchedule.start, 'HH:mm').toDate() : null}
                                                                        max={businessSchedule ? moment(businessSchedule.end, 'HH:mm').toDate() : null}
                                                                        interval={10}
                                                                        useMaskBehavior={true}
                                                                        showClearButton={false}
                                                                        value={timeslotSchedule.start ? moment(timeslotSchedule.start, 'HH:mm:ss').toDate() : null}
                                                                        onValueChanged={handleTimeslotStartChange}
                                                                    >
                                                                    </DateBox>
                                                                }</Observer>
                                                            </div>
                                                        </div>
                                                        <div className='col-6'>
                                                            <Observer>{() =>
                                                                <div className='form-group mb-0 validate validate-required'>
                                                                    <div className='input-group'>
                                                                        <input
                                                                            id='update-timeslot-schedule-duration'
                                                                            type='number'
                                                                            className='form-control'
                                                                            min={5}
                                                                            step={5}
                                                                            value={(timeslotSchedule.duration ? timeslotSchedule.duration : '')}
                                                                            onChange={handleDurationChange}
                                                                        />
                                                                        <div className='input-group-append'>
                                                                            <span className='input-group-text'>min.</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            }</Observer>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='services'>
                                                    <div className='row'>
                                                        <div className='col-12 mb-0'>
                                                            <div className='form-group validate validate-required'>
                                                                <label htmlFor='new-timeslot-services' className='required'>
                                                                    <small>Accepts</small>
                                                                </label>
                                                                <Observer>{() =>
                                                                    <>
                                                                        <Multiselect
                                                                            allowCreate={false}
                                                                            data={getBookingTypes()}
                                                                            defaultValue={timeslotSchedule.bookingTypes}
                                                                            valueField='id'
                                                                            textField='name'
                                                                            tagComponent={({ item }) => (
                                                                                <span
                                                                                    className='tag'
                                                                                    style={{
                                                                                        backgroundColor: '#ff9999',
                                                                                        borderWidth: '1px',
                                                                                        borderStyle: 'solid',
                                                                                        borderColor: '#ff1f1f',
                                                                                        color: '#fff',
                                                                                    }}>
                                                                                    <strong>{item.name}</strong>
                                                                                </span>
                                                                            )}
                                                                            onChange={handleBookingTypeChange}
                                                                        />
                                                                    </>}</Observer>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='repeat'>
                                                    <div className='row'>
                                                        <div className='col-6 mb-0'>
                                                            <div className='form-group'>
                                                                <label htmlFor='new-timeslot-repeat'>
                                                                    <small>Repeat</small>
                                                                </label>
                                                                <Observer>{() =>
                                                                    <>
                                                                        <select
                                                                            id='new-timeslot-repeat'
                                                                            className='custom-select form-control'
                                                                            value={repeatType}
                                                                            onChange={handleRepeatTypeChange}
                                                                        >
                                                                            <option value=''>No repeat</option>
                                                                            <option value='until'>Repeat until</option>
                                                                        </select>
                                                                    </>
                                                                }</Observer>
                                                            </div>
                                                        </div>
                                                        <div className='col-6'>
                                                            <div className='form-group mb-0 validate validate-required'>
                                                                <label htmlFor='new-timeslot-repeat'>
                                                                    <small>&nbsp;</small>
                                                                </label>
                                                                <Observer>{() =>
                                                                    <DateBox
                                                                        id='update-timeslot-schedule-until'
                                                                        type='time'
                                                                        displayFormat={'hh:mm a'}
                                                                        min={businessSchedule ? moment(businessSchedule.start, 'HH:mm').toDate() : null}
                                                                        max={businessSchedule ? moment(businessSchedule.end, 'HH:mm').toDate() : null}
                                                                        interval={10}
                                                                        useMaskBehavior={true}
                                                                        showClearButton={false}
                                                                        disabled={repeatType !== 'until'}
                                                                        value={timeslotSchedule.repeatUntil ? moment(timeslotSchedule.repeatUntil, 'HH:mm:ss').toDate() : null}
                                                                        onValueChanged={handleTimeslotRepeatUntilChange}
                                                                    >
                                                                    </DateBox>
                                                                }</Observer>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                                {
                                                    repeatType === 'until' ?
                                                        <section>
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group'>
                                                                        <label htmlFor='new-timeslot-apply'>
                                                                            <small>Apply to</small>
                                                                        </label>
                                                                        <div className='row my-2 days-of-week'>
                                                                            <div className='col'>
                                                                                <Observer>{() =>
                                                                                    <div className='custom-control custom-checkbox mb-2'>
                                                                                        <input
                                                                                            id='schedule-day-of-week-sunday'
                                                                                            type='checkbox'
                                                                                            name='schedule-day-of-week'
                                                                                            className='custom-control-input'
                                                                                            value='Sunday'
                                                                                            checked={timeslotSchedule.daysOfWeek.some(d => d === 'Sunday')}
                                                                                            onChange={handleDaysOfWeekChange}
                                                                                        />
                                                                                        <label
                                                                                            htmlFor='schedule-day-of-week-sunday'
                                                                                            className='custom-control-label'
                                                                                        >
                                                                                            <br /><small>Sun</small>
                                                                                        </label>
                                                                                    </div>
                                                                                }</Observer>
                                                                            </div>
                                                                            <div className='col'>
                                                                                <Observer>{() =>
                                                                                    <div className='custom-control custom-checkbox mb-2'>
                                                                                        <input
                                                                                            id='schedule-day-of-week-monday'
                                                                                            type='checkbox'
                                                                                            name='schedule-day-of-week'
                                                                                            className='custom-control-input'
                                                                                            value='Monday'
                                                                                            checked={timeslotSchedule.daysOfWeek.some(d => d === 'Monday')}
                                                                                            onChange={handleDaysOfWeekChange}
                                                                                        />
                                                                                        <label
                                                                                            htmlFor='schedule-day-of-week-monday'
                                                                                            className='custom-control-label'
                                                                                        >
                                                                                            <br /><small>Mon</small>
                                                                                        </label>
                                                                                    </div>
                                                                                }</Observer>
                                                                            </div>
                                                                            <div className='col'>
                                                                                <Observer>{() =>
                                                                                    <div className='custom-control custom-checkbox mb-2'>
                                                                                        <input
                                                                                            id='schedule-day-of-week-tuesday'
                                                                                            type='checkbox'
                                                                                            name='schedule-day-of-week'
                                                                                            className='custom-control-input'
                                                                                            value='Tuesday'
                                                                                            checked={timeslotSchedule.daysOfWeek.some(d => d === 'Tuesday')}
                                                                                            onChange={handleDaysOfWeekChange}
                                                                                        />
                                                                                        <label
                                                                                            htmlFor='schedule-day-of-week-tuesday'
                                                                                            className='custom-control-label'
                                                                                        >
                                                                                            <br /><small>Tue</small>
                                                                                        </label>
                                                                                    </div>
                                                                                }</Observer>
                                                                            </div>
                                                                            <div className='col'>
                                                                                <Observer>{() =>
                                                                                    <div className='custom-control custom-checkbox mb-2'>
                                                                                        <input
                                                                                            id='schedule-day-of-week-wednesday'
                                                                                            type='checkbox'
                                                                                            name='schedule-day-of-week'
                                                                                            className='custom-control-input'
                                                                                            value='Wednesday'
                                                                                            checked={timeslotSchedule.daysOfWeek.some(d => d === 'Wednesday')}
                                                                                            onChange={handleDaysOfWeekChange}
                                                                                        />
                                                                                        <label
                                                                                            htmlFor='schedule-day-of-week-wednesday'
                                                                                            className='custom-control-label'
                                                                                        >
                                                                                            <br /><small>Wed</small>
                                                                                        </label>
                                                                                    </div>
                                                                                }</Observer>
                                                                            </div>
                                                                            <div className='col'>
                                                                                <Observer>{() =>
                                                                                    <div className='custom-control custom-checkbox mb-2'>
                                                                                        <input
                                                                                            id='schedule-day-of-week-thursday'
                                                                                            type='checkbox'
                                                                                            name='schedule-day-of-week'
                                                                                            className='custom-control-input'
                                                                                            value='Thursday'
                                                                                            checked={timeslotSchedule.daysOfWeek.some(d => d === 'Thursday')}
                                                                                            onChange={handleDaysOfWeekChange}
                                                                                        />
                                                                                        <label
                                                                                            htmlFor='schedule-day-of-week-thursday'
                                                                                            className='custom-control-label'
                                                                                        >
                                                                                            <br /><small>Thu</small>
                                                                                        </label>
                                                                                    </div>
                                                                                }</Observer>
                                                                            </div>
                                                                            <div className='col'>
                                                                                <Observer>{() =>
                                                                                    <div className='custom-control custom-checkbox mb-2'>
                                                                                        <input
                                                                                            id='schedule-day-of-week-friday'
                                                                                            type='checkbox'
                                                                                            name='schedule-day-of-week'
                                                                                            className='custom-control-input'
                                                                                            value='Friday'
                                                                                            checked={timeslotSchedule.daysOfWeek.some(d => d === 'Friday')}
                                                                                            onChange={handleDaysOfWeekChange}
                                                                                        />
                                                                                        <label
                                                                                            htmlFor='schedule-day-of-week-friday'
                                                                                            className='custom-control-label'
                                                                                        >
                                                                                            <br /><small>Fri</small>
                                                                                        </label>
                                                                                    </div>
                                                                                }</Observer>
                                                                            </div>
                                                                            <div className='col'>
                                                                                <Observer>{() =>
                                                                                    <div className='custom-control custom-checkbox mb-2'>
                                                                                        <input
                                                                                            id='schedule-day-of-week-saturday'
                                                                                            type='checkbox'
                                                                                            name='schedule-day-of-week'
                                                                                            className='custom-control-input'
                                                                                            value='Saturday'
                                                                                            checked={timeslotSchedule.daysOfWeek.some(d => d === 'Saturday')}
                                                                                            onChange={handleDaysOfWeekChange}
                                                                                        />
                                                                                        <label
                                                                                            htmlFor='schedule-day-of-week-saturday'
                                                                                            className='custom-control-label'
                                                                                        >
                                                                                            <br /><small>Sat</small>
                                                                                        </label>
                                                                                    </div>
                                                                                }</Observer>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </section> : null
                                                }
                                            </div>
                                        </FadeIn> : renderQuickDrawerLoading()
                                }
                            </div>
                            <Observer>{() =>
                                timeslotSchedule.isReady ?
                                    <div className='quick-drawer-action'>
                                        <div className='row'>
                                            <div className='col-12'>
                                                <div className='float-right'>
                                                    <button
                                                        type='button'
                                                        className='btn btn-danger mr-2'
                                                        onClick={handleDelete}
                                                    >Delete</button>
                                                    <button
                                                        type='submit'
                                                        className='btn btn-success'
                                                    >Save</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div> : null
                            }</Observer>
                        </div>
                    </fieldset>
                </>
            }</Observer>
        </form>
        <BodyEnd>
            <ConfirmModal ref={confirmModalRef} />
        </BodyEnd>
    </>
}

export default NewTimeslotSchedule;